<template>
  <div class="page-stack">
    <div class="sticky-toolbar">
    <a-card :bordered="false" class="sticky-toolbar-card">
      <a-space wrap align="center">
        <div style="font-weight:600">Заказчик:</div>
        <a-select
          v-model:value="contractorId"
          :options="contractorOptions"
          style="min-width:320px"
          placeholder="Выберите заказчика"
          show-search
          :filter-option="filterContractors"
        />

        <div style="font-weight:600">Дата:</div>
        <a-button @click="shiftDate(-1)"><LeftOutlined /></a-button>
        <a-date-picker v-model:value="dateValue" format="YYYY-MM-DD" :allowClear="false" />
        <a-button @click="shiftDate(1)"><RightOutlined /></a-button>

        <a-button @click="load" :loading="loading" :disabled="!contractorId">Открыть</a-button>
      </a-space>
    </a-card>
    </div>

    <a-alert
      v-if="selectedContractorName"
      type="info"
      show-icon
      :message="`Кухня редактирует заказ: ${selectedContractorName}`"
    />

    <template v-if="contractorId && data">
      <a-card title="Количество порций" :bordered="false">
        <a-row :gutter="[12,12]">
          <a-col :xs="24" :sm="12" :md="6" v-for="m in mealCards" :key="m.meal">
            <a-card size="small">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
                <div style="font-weight:600">{{ m.title }}</div>
              </div>

              <a-input-number
                style="width:100%"
                :min="0"
                v-model:value="localMeals[m.meal]"
              />

              <a-space style="margin-top:10px">
                <a-button type="primary" size="small" @click="saveMeal(m.meal)" :loading="savingMeal === m.meal">
                  Сохранить
                </a-button>
              </a-space>
            </a-card>
          </a-col>
        </a-row>
      </a-card>

      <a-card title="Комментарий" :bordered="false">
        <a-textarea v-model:value="comment" :rows="4" placeholder="Комментарий кухни к заказу..." />
        <a-space style="margin-top:10px">
          <a-button type="primary" @click="saveComment" :loading="savingComment">Сохранить</a-button>
        </a-space>
      </a-card>

      <a-card title="В том числе особые количества" :bordered="false">
        <a-tabs>
          <a-tab-pane v-for="meal in mealKeys" :key="meal" :tab="label(meal)">
            <a-table
              :dataSource="specialTypes"
              :pagination="false"
              rowKey="id"
              size="small"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="column.key === 'name'">
                  {{ record.name }}
                </template>

                <template v-else-if="column.key === 'qty'">
                  <a-input-number
                    :min="0"
                    v-model:value="localSpecials[meal][record.id]"
                    style="width:120px"
                  />
                </template>

                <template v-else-if="column.key === 'save'">
                  <a-button size="small" @click="saveSpecial(meal, record.id)">
                    Сохранить
                  </a-button>
                </template>
              </template>

              <a-table-column key="name" title="Особое" />
              <a-table-column key="qty" title="Количество" />
              <a-table-column key="save" title="" />
            </a-table>
          </a-tab-pane>
        </a-tabs>
      </a-card>

      <a-modal v-model:open="addChildOpen" title="Добавить ребёнка" @ok="saveAddChild">
        <a-form layout="vertical">
          <a-form-item label="Приём пищи">
            <a-input :value="label(addChildMeal)" disabled />
          </a-form-item>

          <a-form-item label="Ребёнок">
            <a-select
              v-model:value="addChildId"
              :options="activeChildOptions"
              style="width:100%"
            />
          </a-form-item>

          <a-form-item label="Примечание">
            <a-textarea v-model:value="addChildNote" :rows="3" />
          </a-form-item>
        </a-form>
      </a-modal>

      <a-card title="Дети с особенностями (в заказе)" :bordered="false">
        <a-tabs>
          <a-tab-pane v-for="m in mealCards" :key="m.meal" :tab="m.title">
            <a-space direction="vertical" style="width:100%" size="middle">
              <a-button @click="openAddChild(m.meal)">Добавить ребёнка</a-button>

              <a-table
                :dataSource="childrenByMeal[m.meal]"
                :pagination="false"
                rowKey="child_id"
                size="small"
              >
                <a-table-column key="full_name" title="Ребёнок" dataIndex="full_name" />
                <a-table-column key="tags" title="Теги">
                  <template #default="{ record }">
                    <ChildTagIcons :tags="record.tags" />
                  </template>
                </a-table-column>
                <a-table-column key="child_comment" title="Комментарий" dataIndex="child_comment" />
                <a-table-column key="order_note" title="Примечание к заказу" dataIndex="order_note" />
                <a-table-column key="actions" title="" width="140">
                  <template #default="{ record }">
                    <a-button danger size="small" @click="deleteChild(m.meal, record.child_id)">
                      Удалить
                    </a-button>
                  </template>
                </a-table-column>
              </a-table>
            </a-space>
          </a-tab-pane>
        </a-tabs>
      </a-card>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { getSpecialTypes } from '../../api/specialTypes'
import { getKitchenContractors, getKitchenContractorChildren } from '../../api/contractors'
import {
  getKitchenOrderByContractorDate,
  setKitchenMealQty,
  setKitchenOrderComment,
  setKitchenOrderSpecialQty,
  addKitchenChildToMeal,
  removeKitchenChildFromMeal,
} from '../../api/kitchenOrders'
import ChildTagIcons from '../../components/ChildTagIcons.vue'
import { label } from '../../utils/labels'

const mealKeys = ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER']
const mealCards = [
  { meal: 'BREAKFAST', title: 'Завтрак' },
  { meal: 'LUNCH', title: 'Обед' },
  { meal: 'SNACK', title: 'Полдник' },
  { meal: 'DINNER', title: 'Ужин' },
]

const loading = ref(false)
const savingMeal = ref('')
const savingComment = ref(false)
const contractorId = ref(null)
const dateValue = ref(dayjs())

const contractors = ref([])
const specialTypes = ref([])
const allChildren = ref([])
const data = ref(null)
const comment = ref('')
const localMeals = reactive({ BREAKFAST: 0, LUNCH: 0, SNACK: 0, DINNER: 0 })
const localSpecials = ref({
  BREAKFAST: {},
  LUNCH: {},
  SNACK: {},
  DINNER: {},
})

const addChildOpen = ref(false)
const addChildMeal = ref('BREAKFAST')
const addChildId = ref(null)
const addChildNote = ref('')

const contractorOptions = computed(() =>
  contractors.value
    .filter(x => x.active)
    .map(x => ({ value: x.id, label: x.name }))
)

const selectedContractorName = computed(() =>
  contractors.value.find(x => x.id === contractorId.value)?.name || ''
)

const childrenByMeal = computed(() =>
  data.value?.children || { BREAKFAST: [], LUNCH: [], SNACK: [], DINNER: [] }
)
const activeChildOptions = computed(() =>
  allChildren.value
    .filter(child => !!child.active)
    .map(child => ({ value: child.id, label: child.full_name }))
)

function filterContractors(input, option) {
  return String(option?.label || '').toLowerCase().includes(String(input).toLowerCase())
}

function dateStr() {
  return dateValue.value.format('YYYY-MM-DD')
}

function shiftDate(days) {
  dateValue.value = dayjs(dateValue.value).add(days, 'day')
}

function buildSpecialsMap(orderSpecials, types) {
  const result = {
    BREAKFAST: {},
    LUNCH: {},
    SNACK: {},
    DINNER: {},
  }

  for (const meal of mealKeys) {
    for (const st of types) {
      result[meal][st.id] = 0
    }
  }

  for (const meal of mealKeys) {
    const items = orderSpecials?.[meal] || []
    for (const item of items) {
      result[meal][item.special_type_id] = item.qty
    }
  }

  return result
}

async function loadDictionaries() {
  try {
    const [contractorsRes, specialRes] = await Promise.all([
      getKitchenContractors(),
      getSpecialTypes(),
    ])

    contractors.value = contractorsRes.data.data || []
    specialTypes.value = specialRes.data.data || []

    if (!contractorId.value) {
      contractorId.value = contractors.value.find(x => x.active)?.id ?? null
    }
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить справочники')
  }
}

async function load() {
  if (!contractorId.value) return

  loading.value = true
  try {
    const [orderRes, childrenRes] = await Promise.all([
      getKitchenOrderByContractorDate(contractorId.value, dateStr()),
      getKitchenContractorChildren(contractorId.value),
    ])

    data.value = orderRes.data.data
    comment.value = data.value?.order?.comment || ''
    allChildren.value = childrenRes.data.data || []

    for (const k of Object.keys(localMeals)) {
      localMeals[k] = data.value?.meals?.[k] ?? 0
    }

    localSpecials.value = buildSpecialsMap(data.value?.specials, specialTypes.value)
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить заказ')
  } finally {
    loading.value = false
  }
}

async function saveMeal(meal) {
  if (!contractorId.value) return

  savingMeal.value = meal
  try {
    await setKitchenMealQty(contractorId.value, dateStr(), meal, localMeals[meal])
    message.success('Сохранено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
  } finally {
    savingMeal.value = ''
  }
}

async function saveComment() {
  if (!contractorId.value) return

  savingComment.value = true
  try {
    await setKitchenOrderComment(contractorId.value, dateStr(), comment.value)
    message.success('Комментарий сохранён')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
  } finally {
    savingComment.value = false
  }
}

async function saveSpecial(meal, specialTypeId) {
  if (!contractorId.value) return

  try {
    const qty = Number(localSpecials.value[meal][specialTypeId] || 0)
    await setKitchenOrderSpecialQty(contractorId.value, dateStr(), meal, specialTypeId, qty)
    message.success('Сохранено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка сохранения')
  }
}

function openAddChild(meal) {
  addChildMeal.value = meal
  addChildId.value = null
  addChildNote.value = ''
  addChildOpen.value = true
}

async function saveAddChild() {
  if (!contractorId.value) return

  if (!addChildId.value) {
    message.warning('Выберите ребёнка')
    return
  }

  try {
    await addKitchenChildToMeal(contractorId.value, dateStr(), addChildId.value, addChildMeal.value, addChildNote.value)
    addChildOpen.value = false
    message.success('Ребёнок добавлен')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка добавления')
  }
}

async function deleteChild(meal, childId) {
  if (!contractorId.value) return

  try {
    await removeKitchenChildFromMeal(contractorId.value, dateStr(), childId, meal)
    message.success('Удалено')
    await load()
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Ошибка удаления')
  }
}

watch(contractorId, () => {
  if (contractorId.value) load()
})

watch(dateValue, () => {
  if (contractorId.value) load()
})

onMounted(async () => {
  await loadDictionaries()
  if (contractorId.value) await load()
})
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.sticky-toolbar {
  position: sticky;
  top: 16px;
  z-index: 20;
}

.sticky-toolbar-card :deep(.ant-card-body) {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

@media (max-width: 767px) {
  .sticky-toolbar {
    top: 8px;
  }
}
</style>
